import { Article } from './article';
import { PlayerWithStats } from './playerListQuery';
import { Club } from './team';

/**
 * Global site search (SC-1096). Shared between the `GET /search` endpoint and the header's
 * search drawer, so the two cannot drift on the shape of a result.
 */

/**
 * One group of results.
 *
 * `items` is capped server-side; `total` is the true match count, which is what the drawer's
 * "14 results for …" line sums. The two differ whenever a search matches more than the cap.
 */
export type SearchSection<T> = {
  items: T[];
  total: number;
};

/**
 * The shapes below are deliberately the ones the site's existing cards already consume — the
 * drawer renders `ClubCard`, `PlayerCard`, `ArticleCard` and `PageCard`, so a club reads like a
 * club everywhere on the site. A leaner projection would have forced bespoke result cards, which
 * is exactly the drift `packages/ui/CLAUDE.md` warns about.
 *
 * Deep-links to `/clubs/:slug`.
 */
export type ClubSearchResult = Pick<
  Club,
  'id' | 'slug' | 'fullName' | 'shortName' | 'threeLetterName' | 'nickName' | 'logo'
>;

/**
 * Exactly what `PlayerCard` takes. Squad members arrive with current-season appearances and goals;
 * players matched only by name carry an empty `stats`, and the card omits the stat row for them.
 *
 * Deep-links to `/players/:id` — players carry a slug (SC-1108) but there is no slug route.
 */
export type PlayerSearchResult = PlayerWithStats;

/** What `ArticleCard` reads. Deep-links to `/articles/:slug`. */
export type ArticleSearchResult = Pick<
  Article,
  'id' | 'slug' | 'title' | 'imageSrc' | 'publishDate' | 'tags' | 'summary' | 'author'
>;

/**
 * A page on this tenant's own site rather than a record — "stats" has to find the Stats page,
 * not only the players on it. Matched in the browser against the navigation the header already
 * holds, so it never appears in the API response.
 */
export type PageSearchResult = {
  title: string;
  path: string;
  /** Breadcrumb to the page, e.g. "Stats › Players" — the card's second line. */
  trail?: string;
  /** From the nav node: `false` opens the link in a new tab, as it does everywhere else in the nav. */
  sameTab?: boolean;
};

/** The `GET /search` response. Pages are added client-side and are not part of it. */
export type SearchResults = {
  clubs: SearchSection<ClubSearchResult>;
  players: SearchSection<PlayerSearchResult>;
  news: SearchSection<ArticleSearchResult>;
};

/** Sections the drawer renders, in render order. */
export const SEARCH_SECTIONS = ['clubs', 'players', 'news', 'pages'] as const;
export type SearchSectionKey = (typeof SEARCH_SECTIONS)[number];

/**
 * Results returned per section (SC-1096 D4).
 *
 * Shared rather than declared on each side: the API caps its queries here and the drawer decides
 * from the same number whether a section may have more behind it. Two independent literals would
 * drift, and the count line would then lie in one direction or the other.
 */
export const SEARCH_SECTION_LIMIT = 10;

/** Characters required before a search runs. Mirrors `SearchQueryDto`'s server-side minimum. */
export const SEARCH_MIN_CHARS = 2;

/** Debounce applied to the field, matching the existing `useTeamsSearchLazyQuery` pattern. */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * The point at which a section's `total` stops being exact.
 *
 * The API bounds its counts here so an unindexed filter cannot turn into an unbounded collection
 * scan on a public endpoint. A section reporting exactly this many may have more, so the drawer
 * renders it as "100+" rather than claiming a precise figure it does not have.
 */
export const SEARCH_COUNT_CEILING = 100;
